import { Component, inject, Injectable, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormGroup, NgForm, FormsModule } from "@angular/forms";
import { Data, Params } from "@angular/router";
import { finalize, Observable, of } from "rxjs";
import { ChangeDetectionService } from "../../shared/services/change-detection.service";
import { DialogService } from "../../shared/services/dialog.service";
import { LoadingService } from "../../shared/services/loading-overlay/loading-overlay.component";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { AppDateAdapter, APP_DATE_FORMATS } from "../../shared/format-datepicker";
import { QuoteModalService } from "./quote-modal.service";
import { QuoteStatus } from "./types/Quote";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { VendorQuoteFilterComponent } from "../../components/vendor-quote-filters/vendor-quote-filters.component";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { ScrollInsetShadowDirective } from "../../directives/scroll-inset-shadow.directive";

interface ShowQuoteModalData {
  /** Required */
  VendorId: number;
}

@Component({
  selector: 'quote-create-modal',
  templateUrl: 'quote-create-modal.component.html',
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    LoadingService, ChangeDetectionService, DialogService, QuoteModalService
  ],
  standalone: true,
  imports: [VendorQuoteFilterComponent, FormsModule, MatDialogModule, MatButtonModule, MatSelectModule, ScrollInsetShadowDirective]
})
export class QuoteCreateModalComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<QuoteCreateModal, number>>(MatDialogRef);
  private showModalData = inject<ShowQuoteModalData>(MAT_DIALOG_DATA);
  private snackbar = inject(MatSnackBar);
  private loadingService = inject(LoadingService);
  private quoteService = inject(QuoteModalService);
  private dialogService = inject(DialogService);
  private changeDetectionService: ChangeDetectionService = new ChangeDetectionService(this.dialogService);

  ngOnInit() {
    this.Quote.VendorId = this.showModalData.VendorId;
  }

  protected Quote = {
    Id: <number>undefined,
    VendorId: <number>undefined,
    Year: <number>undefined,
    QuoteNumber: <string>undefined,
    QuoteNumberSequence: <number>undefined,
    FieldScientistId: <number>undefined,
    FieldScientistName: <string>undefined,
    ContactId: <number>undefined,
    ContactName: <string>undefined,
    PurchaseRequisitionNumber: <string>undefined,
    PurchaseOrderNumber: <string>undefined,
    PurchaseOrderStartDate: <Date>undefined,
    PurchaseOrderEndDate: <Date>undefined,
    CreatedByPersonId: <number>undefined,
    CreatedDate: <Date>undefined,
    UpdatedByPersonId: <number>undefined,
    UpdatedDate: <Date>undefined,
  };

  protected SaveQuote(form: NgForm) {
    if (!this.CheckFormIsValid(form)) return;

    const loadingRef = this.loadingService.StartWaiting("Creating new Quote");
    this.quoteService.CreateQuote(this.Quote.VendorId, {
      ContactId: this.Quote.ContactId,
      FieldScientistId: this.Quote.FieldScientistId,
      Year: this.Quote.Year,
      PurchaseRequisitionNumber: this.Quote.PurchaseRequisitionNumber,
      PurchaseOrderNumber: this.Quote.PurchaseOrderNumber,
      PurchaseOrderStartDate: this.Quote.PurchaseOrderStartDate,
      PurchaseOrderEndDate: this.Quote.PurchaseOrderEndDate,
      Comments: null,
      Status: QuoteStatus.New,
      VendorAssignedQuoteNumber: null,
      PaymentLineItemAssocUpdates: [],
      DocumentIds: []
    })
      .pipe(finalize(() => loadingRef.Stop()))
      .subscribe((result) => this.dialogRef.close(result.Id));
  }

  private GetControls(formGroup: UntypedFormGroup): AbstractControl[] {
    if (!formGroup || !formGroup.controls) return [];

    let controls: Array<AbstractControl> = [];
    for (const ctrlKey in formGroup.controls) {
      const control = formGroup.controls[ctrlKey];
      if (control instanceof UntypedFormGroup) {
        controls = controls.concat(this.GetControls(control));
      } else {
        controls.push(control);
      }
    }

    return controls;
  }

  private CheckFormIsValid(form: NgForm) {
    let hasErrors = false;

    const controls: AbstractControl[] = this.GetControls(form.form);
    for (const ctrl of controls) {
      if (ctrl.errors) {
        ctrl.markAllAsTouched();
        hasErrors = true;
      }
    }

    if (hasErrors) {
      this.snackbar.open("There are validation errors. Please fix the highlighted fields and try again.", null, {
        duration: 5000,
      });
    }

    return !hasErrors;
  }

  protected CancelModal() {
    this.CheckForChangesAndPrompt().subscribe((shouldLeave) => {
      if (!shouldLeave) return;
      this.dialogRef.close(undefined);
    });
  }

  private CheckForChangesAndPrompt(): Observable<boolean> {
    if (this.changeDetectionService.HasChanges(this.Quote)) {
      return this.changeDetectionService.HasChangeDialog();
    }
    return of(true);
  }
}

@Injectable()
export class QuoteCreateModal {
  private dialog = inject(MatDialog);

  public ShowStandaloneModal = (_data: Data, params: Params): Observable<any> => this.ShowModal({
    VendorId: Number(params.get('vendorId'))
  });

  /** Displays the Quote modal. Returns the Quote id when the user closes the modal, or undefined if no Quote was created
   * @returns {Observable<number | undefined>} The Quote ID or undefined if no vendor was created.
   */
  public ShowModal = (data: ShowQuoteModalData): Observable<number | undefined> => {
    const ref = this.dialog.open(QuoteCreateModalComponent, {
      data: data,
      width: "800px",
      disableClose: true,
      autoFocus: false,
    });

    return ref.afterClosed();
  }
}
